import { useEffect, useRef, useState } from "react";
import { Send } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

export default function PortalMessages() {
  const { user } = useAuth();
  const [messages, setMessages] = useState([]);
  const [body, setBody] = useState("");
  const [busy, setBusy] = useState(false);
  const endRef = useRef(null);

  const load = () => api.get("/portal/messages").then((r) => setMessages(r.data.messages || []));
  useEffect(() => { load(); }, []);
  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages]);

  const send = async (e) => {
    e.preventDefault();
    if (!body.trim()) return;
    setBusy(true);
    try {
      await api.post("/portal/messages", { body });
      setBody("");
      await load();
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="px-6 md:px-14 py-14 md:py-20 max-w-3xl flex flex-col h-[calc(100vh-0px)] md:h-screen">
      <p className="eyebrow">Concierge</p>
      <h1 className="mt-4 font-serif text-4xl md:text-5xl text-jade font-light leading-[1.05]">In touch with your concierge.</h1>

      <div data-testid="portal-messages" className="flex-1 overflow-y-auto mt-10 space-y-6 pr-2">
        {messages.length === 0 ? (
          <p className="text-ink/55 text-[14px] italic">No messages yet. Write a note below.</p>
        ) : messages.map((m) => {
          const fromClient = m.from === "client";
          return (
            <div key={m.id} data-testid={`msg-${m.id}`} className={`max-w-[85%] ${fromClient ? "ml-auto text-right" : ""}`}>
              <p className="eyebrow text-ink/55">{m.from_name} · {new Date(m.sent_at).toLocaleString("en-GB", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" })}</p>
              <div className={`mt-2 p-5 ${fromClient ? "bg-jade text-ivory" : "bg-sand text-ink"}`}>
                <p className="text-[15px] font-light leading-relaxed">{m.body}</p>
              </div>
            </div>
          );
        })}
        <div ref={endRef} />
      </div>

      <form onSubmit={send} className="mt-6 flex items-end gap-4 border-t border-mist/60 pt-6">
        <input
          data-testid="msg-input"
          value={body}
          onChange={(e) => setBody(e.target.value)}
          placeholder={`Write to your concierge, ${user?.name?.split(" ")[0] || "traveller"}…`}
          className="input-line"
        />
        <button data-testid="msg-send" disabled={busy || !body.trim()} className="btn-primary inline-flex items-center gap-2 disabled:opacity-50">
          <Send strokeWidth={1.5} className="w-4 h-4" /> Send
        </button>
      </form>
    </div>
  );
}
